import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import Button from '../../../components/ui/Button'
import { peopleSummary } from '../peopleSummary'
import type { ManagerList } from '../types'

interface ColumnOpts {
  t: TFunction
  /** İşlem süren satır — sil düğmesi kilitlenir (çift istek olmasın). */
  busyId: number | null
  onEdit: (row: ManagerList) => void
  onDelete: (row: ManagerList) => void
  /** 126 — Kısıtlı Yetkili asistan: düzenle/sil kolonu hiç çizilmez (salt okunur). */
  readOnly?: boolean
}

/**
 * Liste tablosunun sütunları: Liste Adı · Kişi Sayısı · Kişiler · Grup (122) · işlemler.
 *
 * <p><b>Kişiler kolonu özet gösterir</b> (ilk birkaç ad, sonrası "+N"): tam liste düzenleme pop-up'ında
 * görünür. Hepsini satıra basmak tabloyu okunmaz yapardı; hiç göstermemek ise kullanıcıyı "hangi liste
 * bu?" sorusu için her satırı açmaya zorlardı.
 *
 * <p>Durum (Aktif/Pasif) kolonu <b>yoktur</b> — kullanıcı kararı 30.09.2026: bu ekranda yalnız
 * ekle/düzenle/sil vardır (emsal: Toplantı Grupları). Silme yumuşak silmedir, kullanıcıya durum olarak
 * sunulmaz.
 */
export function managerListColumns({
  t,
  busyId,
  onEdit,
  onDelete,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  const columns = [
    <Column
      key="name"
      field="name"
      header={t('managerLists.columns.name')}
      sortable
      body={(row: ManagerList) => row.name}
    />,
    <Column
      key="personCount"
      field="personCount"
      header={t('managerLists.columns.personCount')}
      style={{ width: '9rem' }}
      sortable
    />,
    <Column
      key="people"
      header={t('managerLists.columns.people')}
      body={(row: ManagerList) => peopleSummary(row, t('managerLists.form.noPeople'))}
    />,
    <Column
      key="groupName"
      field="groupName"
      header={t('recordGroups.column')}
      style={{ width: '14rem' }}
      body={(row: ManagerList) => row.groupName ?? ''}
    />,
  ]
  if (readOnly) {
    // Salt okunur kip (126): yazma aksiyonları yerine yalnız "Görüntüle" — listenin kişilerinin tamamı
    // yalnız pop-up'ta görünür (kişi kolonu ilk üçünü gösterir).
    return [
      ...columns,
      <Column
        key="actions"
        header={t('managerLists.columns.actions')}
        style={{ width: '6rem' }}
        body={(row: ManagerList) => (
          <div className="admin-row-actions">
            <Button
              icon="pi pi-eye"
              rounded
              text
              aria-label={t('managerLists.actions.viewNamed', { name: row.name })}
              tooltip={t('managerLists.actions.view')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onEdit(row)}
            />
          </div>
        )}
      />,
    ]
  }
  return [
    ...columns,
    <Column
      key="actions"
      header={t('managerLists.columns.actions')}
      style={{ width: '9rem' }}
      body={(row: ManagerList) => (
        <div className="admin-row-actions">
          <Button
            icon="pi pi-pencil"
            rounded
            text
            aria-label={t('managerLists.actions.editNamed', { name: row.name })}
            tooltip={t('managerLists.actions.edit')}
            tooltipOptions={{ position: 'bottom' }}
            onClick={() => onEdit(row)}
          />
          <Button
            icon="pi pi-trash"
            rounded
            text
            severity="danger"
            aria-label={t('managerLists.actions.deleteNamed', { name: row.name })}
            tooltip={t('managerLists.actions.delete')}
            tooltipOptions={{ position: 'bottom' }}
            disabled={busyId === row.id}
            onClick={() => onDelete(row)}
          />
        </div>
      )}
    />,
  ]
}
